// ©2024 Austin App House. All rights reserved.
//
// React 16 wrapper that hosts a federated micro-frontend.
//
// It loads an exposed `mount(domNode, props)` function from a remote and lets
// the remote render with its OWN React root. This isolates the remote's React
// (18) from the host's React (16), avoiding the shared-singleton version clash.

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { loadRemoteModule } from './loadRemote';
import Loader from '../components/dashboard/common/Loader';

function sweepStaleMarketingModals() {
  if (typeof window === 'undefined') return;
  if (window.__WURQLY_MODAL_ALLOWED) return;
  if (typeof window.__WURQLY_SWEEP_MODALS === 'function') {
    window.__WURQLY_SWEEP_MODALS();
    return;
  }
  if (typeof window.__WURQLY_ALLOW_MODAL === 'function') {
    window.__WURQLY_ALLOW_MODAL(false);
  }
}

// RemoteMount catches remote failures to show a fallback, so Sentry's global
// handlers never see them; report them here.
function reportRemoteError(err, moduleId, phase) {
  Sentry.captureException(err, { tags: { remote_module: moduleId, remote_phase: phase } });
}

function RemoteMount({ module: moduleId, remoteProps }) {
  const containerRef = useRef(null);
  const unmountRef = useRef(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const pathname = remoteProps && remoteProps.pathname;
  const search = remoteProps && remoteProps.search;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    sweepStaleMarketingModals();

    if (typeof unmountRef.current === 'function') {
      try {
        unmountRef.current();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[RemoteMount] error during remount unmount', err);
        reportRemoteError(err, moduleId, 'unmount');
      }
      unmountRef.current = null;
    }

    let phase = 'load';
    loadRemoteModule(moduleId)
      .then((mount) => {
        if (!active || !containerRef.current) return;
        phase = 'mount';
        if (typeof mount !== 'function') {
          throw new Error(`Remote module "${moduleId}" did not export a mount function.`);
        }
        unmountRef.current = mount(containerRef.current, remoteProps);
        setLoading(false);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(`[RemoteMount] failed to ${phase} remote`, moduleId, err);
        // An ended effect's failure is reported by the effect that replaced it,
        // which loads the same shared script.
        if (!active) return;
        reportRemoteError(err, moduleId, phase);
        setError(err);
        setLoading(false);
      });

    return () => {
      active = false;
      if (typeof unmountRef.current === 'function') {
        try {
          unmountRef.current();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[RemoteMount] error during unmount', err);
          reportRemoteError(err, moduleId, 'unmount');
        }
        unmountRef.current = null;
      }
      if (typeof window.__WURQLY_ALLOW_MODAL === 'function') {
        window.__WURQLY_ALLOW_MODAL(false);
      }
    };
  }, [moduleId, pathname, search]);

  if (error) {
    return (
      <div style={{ color: '#b00020', fontFamily: 'system-ui, sans-serif' }}>
        Failed to load remote module “{moduleId}”.
      </div>
    );
  }

  return (
    <div className="remote-mount">
      {loading ? (
        <div className="dashboard-page-loader remote-mount-loader">
          <Loader size="lg" className="wurqly-spiral-loader-wrap--page" />
        </div>
      ) : null}
      <div ref={containerRef} className="remote-mount-host" />
    </div>
  );
}

RemoteMount.propTypes = {
  module: PropTypes.string,
  remoteProps: PropTypes.object,
};

RemoteMount.defaultProps = {
  module: 'nextjsRemote/mount',
  remoteProps: {},
};

export default RemoteMount;
